import React, { createContext, useContext, useState, useCallback, useEffect, useRef } from 'react'
import { trackEvent } from '@/lib/analytics'
import { toast } from 'sonner'
import { createVoiceService, defaultVoiceConfig } from '@/services/voice'
import type { VoiceServiceProvider } from '@/services/voice'
import { useAuth } from '@/contexts/AuthContext'
import { voiceApiService } from '@/services/voiceApi'
import { pricingApiService } from '@/services/pricingApi'
import { CallState, CallStatus } from '@/types/call'
import type { VoiceService, VoiceConnectionStatus, AudioDevices, DeviceStatus, NetworkWarningName, NetworkQuality, CallerIdMode } from '@/services/voice'
import { NETWORK_WARNING_INFO } from '@/services/voice'
import { MockVoiceService } from '@/services/voice'
import { useTranslation } from 'react-i18next'

const PROVIDER_STORAGE_KEY = 'voice_provider_override'
const PREFERRED_INPUT_LABEL_KEY = 'preferred_audio_input_label'

function getStoredProvider(): VoiceServiceProvider | null {
  try {
    const stored = localStorage.getItem(PROVIDER_STORAGE_KEY)
    if (stored === 'twilio' || stored === 'mock') return stored
  } catch {}
  return null
}

/** Pick the best input device.
 *  Priority: saved label > "default" > "built-in" > non-iPhone/iPad/Continuity > anything */
function pickPreferredInput(
  inputs: { deviceId: string; label: string }[],
): { deviceId: string; label: string } | null {
  if (!inputs.length) return null
  let savedLabel: string | null = null
  try { savedLabel = localStorage.getItem(PREFERRED_INPUT_LABEL_KEY) } catch {}
  if (savedLabel) {
    const saved = inputs.find(d => d.label === savedLabel)
    if (saved) return saved
  }
  const find = (pred: (d: { deviceId: string; label: string }) => boolean) =>
    inputs.find(pred)
  return (
    find(d => d.deviceId === 'default' || /\bdefault\b/i.test(d.label)) ??
    find(d => /built.?in/i.test(d.label)) ??
    find(d => !/iphone|ipad|continuity/i.test(d.label)) ??
    inputs[0]
  )
}

export interface VoiceServiceContextValue {
  isReady: boolean
  deviceStatus: DeviceStatus
  networkQuality: NetworkQuality
  activeNetworkWarnings: NetworkWarningName[]
  callState: CallState
  error: string | null
  isMock: boolean
  provider: VoiceServiceProvider
  setProvider: (provider: VoiceServiceProvider) => void
  isMuted: boolean
  audioDevices: AudioDevices
  startCall: (phoneNumber: string, options?: { callerIdMode?: CallerIdMode; callerIdNumber?: string }) => Promise<void>
  endCall: () => void
  sendDTMF: (digit: string) => void
  clearError: () => void
  toggleMute: () => void
  setInputDevice: (deviceId: string) => Promise<void>
  setOutputDevice: (deviceId: string) => Promise<void>
  refreshAudioDevices: () => void
  /** Request voice service initialization (lazy — only initializes once) */
  requestInit: () => void
  /** Mock-only: trigger a network warning event */
  triggerNetworkWarning: (name: NetworkWarningName) => void
  /** Mock-only: clear a network warning event */
  clearNetworkWarning: (name: NetworkWarningName) => void
}

const VoiceServiceContext = createContext<VoiceServiceContextValue | null>(null)

// Map voice connection status to call status
const mapStatusToCallStatus = (status: VoiceConnectionStatus): CallStatus => {
  switch (status) {
    case 'connecting': return 'connecting'
    case 'ringing': return 'ringing'
    case 'connected': return 'in-progress'
    case 'reconnecting': return 'reconnecting'
    case 'disconnected': return 'ended'
    case 'failed': return 'failed'
    default: return 'idle'
  }
}

export function VoiceServiceProvider({ children }: { children: React.ReactNode }) {
  const { isAuthenticated } = useAuth()
  const { t } = useTranslation('app')

  const [isReady, setIsReady] = useState(false)
  const [deviceStatus, setDeviceStatus] = useState<DeviceStatus>('registering')
  const [activeNetworkWarnings, setActiveNetworkWarnings] = useState<NetworkWarningName[]>([])
  const [callState, setCallState] = useState<CallState>({ status: 'idle' })
  const [error, setError] = useState<string | null>(null)
  const [isMuted, setIsMuted] = useState(false)
  const [audioDevices, setAudioDevices] = useState<AudioDevices>({
    inputs: [],
    outputs: [],
    selectedInput: null,
    selectedOutput: null
  })

  const [provider, setProviderState] = useState<VoiceServiceProvider>(
    getStoredProvider() ?? defaultVoiceConfig.provider
  )

  const setProvider = useCallback((newProvider: VoiceServiceProvider) => {
    try { localStorage.setItem(PROVIDER_STORAGE_KEY, newProvider) } catch {}
    setProviderState(newProvider)
    setIsReady(false)
    setDeviceStatus('registering')
    initializingRef.current = false
    if (serviceRef.current) {
      destroyingRef.current = true
      serviceRef.current.destroy()
      destroyingRef.current = false
      serviceRef.current = null
    }
  }, [])

  const serviceRef = useRef<VoiceService | null>(null)
  const initializingRef = useRef(false)
  const destroyingRef = useRef(false)
  const [initRequested, setInitRequested] = useState(false)

  const requestInit = useCallback(() => {
    setInitRequested(true)
  }, [])

  // Initialize voice service
  const initialize = useCallback(async () => {
    if (!isAuthenticated || initializingRef.current) {
      return
    }

    try {
      initializingRef.current = true
      setError(null)

      // Get access token (skip for mock service)
      let token = 'mock-token'
      if (provider !== 'mock') {
        const tokenResponse = await voiceApiService.getTwilioAccessToken()
        token = tokenResponse.token
      }

      // Create and initialize service
      if (serviceRef.current) {
        destroyingRef.current = true
        serviceRef.current.destroy()
        destroyingRef.current = false
      }

      serviceRef.current = createVoiceService({
        provider,
        getNewToken: async () => {
          const response = await voiceApiService.getTwilioAccessToken()
          return response.token
        },
      })

      await serviceRef.current.initialize(token, {
        onDeviceReady: () => {
          setIsReady(true)
          setDeviceStatus('registered')
          setError(null)
        },

        onDeviceRegistered: () => {
          setIsReady(true)
          setDeviceStatus('registered')
          setError(null)
        },

        onDeviceUnregistered: () => {
          setIsReady(false)
          setDeviceStatus('unregistered')
          if (!destroyingRef.current) {
            toast.warning(t('voice.deviceOffline', { defaultValue: 'Voice service offline. Reconnecting...' }))
          }
        },

        onDeviceError: (err) => {
          setError(err.message)
          setIsReady(false)
          setDeviceStatus('error')
        },

        onConnectionStatusChange: (connection) => {
          setCallState(prev => ({
            ...prev,
            status: mapStatusToCallStatus(connection.status),
            phoneNumber: connection.phoneNumber,
            startTime: connection.startTime,
            duration: connection.duration || 0
          }))
        },

        onCallEnded: (connection) => {
          const duration = connection.duration || 0

          setCallState({
            status: 'ended',
            duration
          })
          setActiveNetworkWarnings([])

          // Reset to idle after delay
          setTimeout(() => {
            setCallState({ status: 'idle' })
          }, 2000)
        },

        onCallFailed: (_, err) => {
          setCallState(prev => ({
            ...prev,
            status: 'failed',
            errorMessage: err.message
          }))
          setError(err.message)
        },

        onReconnecting: (err) => {
          toast.warning(t('voice.reconnecting'))
          console.warn('Call reconnecting:', err.message)
        },

        onReconnected: () => {
          toast.success(t('voice.reconnected'))
        },

        onWarning: (warningName) => {
          const name = warningName as NetworkWarningName
          const info = NETWORK_WARNING_INFO[name]
          if (!info) return
          setActiveNetworkWarnings(prev => prev.includes(name) ? prev : [...prev, name])
          if (info.severity === 'critical') {
            toast.error(info.label, { description: info.description })
          } else if (info.severity === 'high') {
            toast.warning(info.label, { description: info.description })
          }
        },

        onWarningCleared: (warningName) => {
          const name = warningName as NetworkWarningName
          setActiveNetworkWarnings(prev => prev.filter(w => w !== name))
        },

        onAudioDevicesChanged: () => {
          if (!serviceRef.current) return
          const devices = serviceRef.current.getAudioDevices()
          // Auto-select preferred input if none is currently selected or the
          // previously selected device has disappeared (e.g. unplugged).
          const currentlySelected = devices.selectedInput
          const stillPresent = currentlySelected && devices.inputs.some(d => d.deviceId === currentlySelected)
          if (!stillPresent && devices.inputs.length) {
            const preferred = pickPreferredInput(devices.inputs)
            if (preferred) {
              serviceRef.current.setInputDevice(preferred.deviceId).catch(console.error)
              setAudioDevices({ ...devices, selectedInput: preferred.deviceId })
              return
            }
          }
          setAudioDevices(devices)
        },
      })

    } catch (err) {
      const message = err instanceof Error ? err.message : t('voice.initFailed')
      setError(message)
      toast.error(t('voice.serviceError', { message }))
    } finally {
      initializingRef.current = false
    }
  }, [isAuthenticated, provider, t])

  // Start a call
  const startCall = useCallback(async (phoneNumber: string, options?: { callerIdMode?: CallerIdMode; callerIdNumber?: string }) => {
    if (!serviceRef.current || !isReady) {
      throw new Error(t('voice.serviceNotReady'))
    }

    try {
      setError(null)

      // Validate phone number and get rate
      setCallState({
        status: 'validating',
        phoneNumber,
        startTime: new Date(),
        duration: 0
      })

      const rateResponse = await pricingApiService.getRate(phoneNumber)

      // Number is valid, proceed with call
      setCallState({
        status: 'connecting',
        phoneNumber,
        startTime: new Date(),
        duration: 0,
        rate: rateResponse.price_per_min / 100,
      })

      await serviceRef.current.makeCall({ phoneNumber, callerIdMode: options?.callerIdMode, callerIdNumber: options?.callerIdNumber })
      trackEvent('call-started')

    } catch (err) {
      const message = err instanceof Error ? err.message : t('voice.startCallFailed')
      setError(message)

      // Only update state if not already set to failed by validation
      if (callState.status !== 'failed') {
        setCallState(prev => ({
          ...prev,
          status: 'failed',
          errorMessage: message
        }))
      }

      toast.error(t('voice.callFailed', { message }))
      throw err
    }
  }, [isReady, t, callState.status])

  // End call
  const endCall = useCallback(() => {
    if (serviceRef.current) {
      serviceRef.current.endCall().catch(console.error)
    }
  }, [])

  // Send DTMF
  const sendDTMF = useCallback((digit: string) => {
    if (serviceRef.current) {
      serviceRef.current.sendDTMF(digit).catch(console.error)
    }
  }, [])

  // Clear error
  const clearError = useCallback(() => {
    setError(null)
  }, [])

  // Toggle mute
  const toggleMute = useCallback(() => {
    if (serviceRef.current) {
      const newMuted = !isMuted
      serviceRef.current.setMuted(newMuted)
      setIsMuted(newMuted)
    }
  }, [isMuted])

  // Set input device
  const setInputDevice = useCallback(async (deviceId: string) => {
    if (!serviceRef.current) return
    await serviceRef.current.setInputDevice(deviceId)
    // Persist the label so the same device is preferred next session
    const devices = serviceRef.current.getAudioDevices()
    const device = devices.inputs.find(d => d.deviceId === deviceId)
    if (device) {
      try { localStorage.setItem(PREFERRED_INPUT_LABEL_KEY, device.label) } catch {}
    }
    setAudioDevices(prev => ({ ...prev, selectedInput: deviceId }))
  }, [])

  // Set output device
  const setOutputDevice = useCallback(async (deviceId: string) => {
    if (serviceRef.current) {
      await serviceRef.current.setOutputDevice(deviceId)
      setAudioDevices(prev => ({ ...prev, selectedOutput: deviceId }))
    }
  }, [])

  // Refresh audio devices
  const refreshAudioDevices = useCallback(() => {
    if (serviceRef.current) {
      setAudioDevices(serviceRef.current.getAudioDevices())
    }
  }, [])

  // Mock-only: simulate network warning
  const triggerNetworkWarning = useCallback((name: NetworkWarningName) => {
    if (serviceRef.current instanceof MockVoiceService) {
      serviceRef.current.simulateWarning(name)
    }
  }, [])

  // Mock-only: clear network warning
  const clearNetworkWarning = useCallback((name: NetworkWarningName) => {
    if (serviceRef.current instanceof MockVoiceService) {
      serviceRef.current.clearWarning(name)
    }
  }, [])

  // Initialize when authenticated AND initialization has been requested (lazy)
  useEffect(() => {
    if (isAuthenticated && initRequested && !isReady && !initializingRef.current) {
      initialize()
    }
  }, [isAuthenticated, initRequested, isReady, initialize])

  // Refresh audio devices when ready
  useEffect(() => {
    if (isReady) {
      refreshAudioDevices()
    }
  }, [isReady, refreshAudioDevices])

  // Reset mute state when call ends
  useEffect(() => {
    if (callState.status === 'idle' || callState.status === 'ended') {
      setIsMuted(false)
    }
  }, [callState.status])

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (serviceRef.current) {
        destroyingRef.current = true
        serviceRef.current.destroy()
        destroyingRef.current = false
      }
    }
  }, [])

  const networkQuality: NetworkQuality = (() => {
    if (activeNetworkWarnings.length === 0) return 'good'
    const hasCritical = activeNetworkWarnings.some(
      w => NETWORK_WARNING_INFO[w]?.severity === 'critical'
    )
    const hasHigh = activeNetworkWarnings.some(
      w => NETWORK_WARNING_INFO[w]?.severity === 'high'
    )
    return hasCritical || hasHigh ? 'poor' : 'degraded'
  })()

  const value: VoiceServiceContextValue = {
    isReady,
    deviceStatus,
    networkQuality,
    activeNetworkWarnings,
    callState,
    error,
    isMock: provider === 'mock',
    provider,
    setProvider,
    isMuted,
    audioDevices,
    startCall,
    endCall,
    sendDTMF,
    clearError,
    toggleMute,
    setInputDevice,
    setOutputDevice,
    refreshAudioDevices,
    requestInit,
    triggerNetworkWarning,
    clearNetworkWarning,
  }

  return (
    <VoiceServiceContext.Provider value={value}>
      {children}
    </VoiceServiceContext.Provider>
  )
}

export function useVoiceServiceContext(): VoiceServiceContextValue {
  const context = useContext(VoiceServiceContext)
  if (!context) {
    throw new Error('useVoiceServiceContext must be used within a VoiceServiceProvider')
  }
  return context
}
